// DO Applications Management Component - Converted from .NET rptrAppsList_DataBind
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Box,
    Typography,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    TextField,
    CircularProgress,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    Divider,
    Chip,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Badge,
    Card,
    CardContent,
    Autocomplete,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import { 
    Edit as EditIcon, 
    Delete as DeleteIcon, 
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    AddCircleOutline as AddCircleOutlineIcon,
    VerifiedUser as VerifiedUserIcon,
    Visibility as VisibilityIcon,
    GetApp as GetAppIcon,
    History as HistoryIcon,
    Refresh as RefreshIcon,
    AccessTime as AccessTimeIcon,
    Check as CheckIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import AssessorApiService from '../../../services/AssessorApiService';
import StageService from '../../../services/stageService';
import { encryptAppId, encryptText } from '../../../utils/encryption';
import { toast, ToastContainer } from 'react-toastify';

// Helper functions for displaying data
const getApplicationTypeName = (assessmentTypeId) => {
    const types = {
        1: 'New Application',
        2: 'Renewal',
        3: 'Extension',
        4: 'Surveillance',
        5: 'Re-assessment',
        6: 'Special',
        7: 'Others'
    };
    return types[assessmentTypeId] || 'Unknown';
};

const getDisciplineName = (labTypeId) => {
    const disciplines = {
        1: 'Testing',
        2: 'Calibration',
        7: 'Medical'
    };
    return disciplines[labTypeId] || 'N/A';
};

const getGroupName = (labTypeId) => {
    const groups = {
        1: 'Physical/Chemical',
        2: 'Electrical/Electronics', 
        7: 'Pathology'
    };
    return groups[labTypeId] || 'N/A';
};


const SubmittedApplicationSection = () => {
    // Router hooks
    const navigate = useNavigate();

    // Helper function to parse AssessmentDate field
    const parseAssessmentDate = (assessmentDateStr) => {
        if (!assessmentDateStr) return { start: null, end: null };
        
        // Format: "Date from : 31/10/2025  Date To : 31/12/2025,"
        const dateRegex = /Date from\s*:\s*(\d{2}\/\d{2}\/\d{4})\s*Date To\s*:\s*(\d{2}\/\d{2}\/\d{4})/i;
        const match = assessmentDateStr.match(dateRegex);
        
        if (match) {
            return {
                start: match[1], // DD/MM/YYYY format
                end: match[2]    // DD/MM/YYYY format
            };
        }
        
        return { start: null, end: null };
    };

    // State management
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [selectedAppType, setSelectedAppType] = useState('new');
    const [labTypes, setLabTypes] = useState([]);
    const [selectedLabType, setSelectedLabType] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [summary, setSummary] = useState(null);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [certificationFiles, setCertificationFiles] = useState([]);
    const [scfVisible, setScfVisible] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [labIdFilter, setLabIdFilter] = useState('all');
    const [labNameFilter, setLabNameFilter] = useState('all');
    const [stateFilter, setStateFilter] = useState('all');
    const [daysPassedFilter, setDaysPassedFilter] = useState('all');
    const [workableFilesFilter, setWorkableFilesFilter] = useState(false);
    const [filteredApplications, setFilteredApplications] = useState([]);
    const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [selectedAppForAction, setSelectedAppForAction] = useState(null);
    const [rejectRemarks, setRejectRemarks] = useState('');
    
    // Declaration checkboxes state
    const [declaration1Checked, setDeclaration1Checked] = useState(false);
    const [declaration2Checked, setDeclaration2Checked] = useState(false);
    const [declaration3Checked, setDeclaration3Checked] = useState(false);
    const [declaration4Checked, setDeclaration4Checked] = useState(false);

    const [travelItineraryButtonVisibilityMap, setTravelItineraryButtonVisibilityMap] = useState({});

    // Get user data from localStorage
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    console.log('User Data:', userData);
    const orgId = userData.orgId;

    // Check if assessor has filled required details
    const checkAssessorDetails = useCallback(async () => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/get-basic-details/${orgId}`;
            const resp = await axios.get(url);
            
            if (resp.data.success && resp.data.data) {
                const data = resp.data.data;
                
                // Check if required fields are filled
                const requiredFieldsFilled = 
                    data.residentialLatitude && data.residentialLatitude !== null && 
                    data.residentialLongitude && data.residentialLongitude !== null && 
                    data.officeLatitude && data.officeLatitude !== null && 
                    data.officeLongitude && data.officeLongitude !== null &&
                    //data.exemptFromAllotment && data.exemptFromAllotment !== "" &&
                    (data.orgNABLaccridiated === "Yes" ? (
                        data.FieldType && data.FieldType !== null && 
                        data.Discipline && data.Discipline !== null &&
                        data.Labid && data.Labid !== null
                    ) : (
                        true
                    ));

                //If required fields are not filled and user hasn't skipped, redirect to bio-data page
                const assessorBioDataSkipped = localStorage.getItem('assessorBioDataSkipped');
                
                if (!requiredFieldsFilled && !assessorBioDataSkipped) {
                    navigate('/main/assessor/assessor-bio-data');
                }
            }
        } catch (error) {
            console.error('Error checking assessor details:', error);
        }
    }, [orgId, navigate]);

    // Load applications based on selected type
    const loadApplications = useCallback(async (appType = selectedAppType) => {
        if (!AssessorApiService.validateUserSession()) {
            setError('User session invalid. Please login again.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const data = await AssessorApiService.getApplicationsList({
                orgId,
                appType,
                labTypeId: selectedLabType,
                searchText
            });
            
            if (data.success) {
                setApplications(data.data || []);
                setSelectedAppType(appType);
            } else {
                throw new Error(data.message || 'Failed to load applications');
            }
        } catch (err) {
            setError(`Error loading applications: ${err.message}`);
            console.error('Load applications error:', err);
        } finally {
            setLoading(false);
        }
    }, [orgId, selectedAppType, selectedLabType, searchText]);

    // Load lab types for dropdown
    const loadLabTypes = useCallback(async () => {
        try {
            const data = await AssessorApiService.getLabTypes();
            if (data.success) {
                setLabTypes(data.data || []);
            }
        } catch (err) {
            console.error('Load lab types error:', err);
        }
    }, []);

    // Load certification files
    const loadCertificationFiles = useCallback(async () => {
        try {
            const data = await AssessorApiService.getCertificationFiles(orgId);
            if (data.success) {
                setCertificationFiles(data.data || []);
            }
        } catch (err) {
            console.error('Load certification files error:', err);
        }
    }, [orgId]);

    // Check SCF visibility
    const checkSCFVisibility = useCallback(async () => {
        try {
            const data = await AssessorApiService.checkSCFVisibility(orgId);
            if (data.success) {
                setScfVisible(data.isVisible);
            }
        } catch (err) {
            console.error('Check SCF visibility error:', err);
        }
    }, [orgId]);

    // Calculate summary from applications
    const calculateSummary = useCallback(() => {
        const totalApplications = applications.length;
        const workableFiles = applications.filter(app => {
            const stage = app.Stage || app.Status || '';
            return stage === '18' || stage === '18b' || stage === '18c' || 
                   stage === 18 || stage.toString().toLowerCase() === '18b' || 
                   stage.toString().toLowerCase() === '18c';
        }).length;

        setSummary({
            totalApplications,
            workableFiles,
            filesAtLabEnd: 0, // This would need specific logic
            accreditedApplications: 0 // This would need specific logic
        });
    }, [applications]);

    // View application - navigate to dedicated view page
    const viewApplication = (app) => {
        try {
            console.log('🔍 View Application Debug:');
            console.log('Application data:', app);
            
            // Encrypt parameters
            const encryptedAppId = encryptAppId(app.AppId);
            const encryptedOrgId = encryptText(app.OrgId?.toString() || '');
            const encryptedLabTypeId = encryptText(app.LabTypeId?.toString() || '');
            
            console.log('Encrypted parameters:', {
                appId: encryptedAppId,
                orgId: encryptedOrgId,
                labTypeId: encryptedLabTypeId
            });
            
            // Build URL with encrypted parameters
            const queryParams = new URLSearchParams({
                appId: encryptedAppId,
                orgId: encryptedOrgId,
                labTypeId: encryptedLabTypeId
            });
            
            const targetUrl = `/main/assessor/applications/view?${queryParams.toString()}`;
            console.log('Target URL:', targetUrl);
            
            // Navigate to ViewApplication page
            navigate(targetUrl);
        } catch (err) {
            console.error('Navigate to view application error:', err);
        }
    };

    // Handle button click
    const handleButtonClick = (appType) => {
        // Clear all filters when switching button types
        setWorkableFilesFilter(false);
        setStatusFilter('all');
        setLabIdFilter('all');
        setLabNameFilter('all');
        setStateFilter('all');
        setDaysPassedFilter('all');
        setSearchText('');
        loadApplications(appType);
    };

    // Reset declaration checkboxes
    const resetDeclarationCheckboxes = () => {
        setDeclaration1Checked(false);
        setDeclaration2Checked(false);
        setDeclaration3Checked(false);
        setDeclaration4Checked(false);
    };

    // Handle Accept Application
    const handleAccept = (app) => {
        setSelectedAppForAction(app);
        resetDeclarationCheckboxes(); // Reset checkboxes when opening dialog
        setAcceptDialogOpen(true);
    };

    // Handle Accept Dialog Close
    const handleAcceptDialogClose = () => {
        if (!loading) {
            setAcceptDialogOpen(false);
            setSelectedAppForAction(null);
            resetDeclarationCheckboxes(); // Reset checkboxes when closing dialog
        }
    };

    // Confirm Accept Application
    const confirmAccept = async () => {
        if (!selectedAppForAction) return;
        
        setLoading(true);
        setError(null);
        
        try {
            // Call API to accept application and update assessoralloted table
            await AssessorApiService.acceptApplication({
                appId: selectedAppForAction.AppId,
                orgId: selectedAppForAction.OrgId,
                assessorAllotedId: selectedAppForAction.AssessorAllotedId || selectedAppForAction.Id,
                assessorId: userData.orgId
            });
            toast.success('Application accepted successfully!');
            setSuccess(`Application accepted successfully!`);
            setAcceptDialogOpen(false);
            setSelectedAppForAction(null);
            resetDeclarationCheckboxes(); // Reset checkboxes after successful acceptance
            
            // Reload applications list
            loadApplications(selectedAppType);
            
        } catch (err) {
            console.error('Accept application error:', err);
            setError(`Failed to accept application: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Handle Reject Application
    const handleReject = (app) => {
        setSelectedAppForAction(app);
        setRejectRemarks('');
        setRejectDialogOpen(true);
    };

    // Confirm Reject Application
    const confirmReject = async () => {
        if (!selectedAppForAction) return;
        
        // Validate remarks
        if (!rejectRemarks || rejectRemarks.trim() === '') {
            setError('Please provide remarks for rejection');
            return;
        }
        
        setLoading(true);
        setError(null);
        
        try {
            // Call API to reject application and update assessoralloted table
            await AssessorApiService.rejectApplication({
                appId: selectedAppForAction.AppId,
                orgId: selectedAppForAction.OrgId,
                assessorAllotedId: selectedAppForAction.AssessorAllotedId || selectedAppForAction.Id,
                assessorId: userData.orgId,
                remarks: rejectRemarks
            });
            
            setSuccess(`Application ${selectedAppForAction.ApplicationNo || selectedAppForAction.TempAppNo} rejected successfully!`);
            setRejectDialogOpen(false);
            setSelectedAppForAction(null);
            setRejectRemarks('');
            
            // Reload applications list
            loadApplications(selectedAppType);
            
        } catch (err) {
            console.error('Reject application error:', err);
            setError(`Failed to reject application: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Handle search
    const handleSearch = () => {
        loadApplications(selectedAppType);
    };

    // Handle Excel export
    const handleExportToExcel = async () => {
        try {
            setLoading(true);
            const blob = await AssessorApiService.exportToExcel({
                orgId,
                appType: selectedAppType,
                labTypeId: selectedLabType,
                searchText
            });
            
            AssessorApiService.downloadExcelFile(blob, `Assessor_${selectedAppType}_${new Date().toISOString().split('T')[0]}.xlsx`);
        } catch (err) {
            setError(`Error exporting to Excel: ${err.message}`);
            console.error('Export error:', err);
        } finally {
            setLoading(false);
        }
    };



    // Format date
    const formatDate = (dateString) => {
        return AssessorApiService.formatDate(dateString);
    };

    // Calculate days passed from status date
    const calculateDaysPassed = (statusDate) => {
        if (!statusDate) return 'N/A';
        
        const status = new Date(statusDate);
        const today = new Date();
        
        // Reset time to midnight for accurate day calculation
        status.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        
        const diffTime = today - status;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        
        return diffDays;
    };

    // Get unique statuses from applications
    const getUniqueStatuses = () => {
        const statuses = new Set();
        applications.forEach(app => {
            const status = app.Status || app.Stage;
            if (status) {
                statuses.add(status);
            }
        });
        return Array.from(statuses).sort();
    };

    // Get unique Lab IDs from applications
    const getUniqueLabIds = () => {
        const labIds = new Set();
        applications.forEach(app => {
            const labId = app.ApplicationNo || app.TempAppNo || `TEMP-${app.AppId}`;
            if (labId) {
                labIds.add(labId);
            }
        });
        return Array.from(labIds).sort();
    };

    // Get unique Laboratory Names from applications
    const getUniqueLabNames = () => {
        const labNames = new Set();
        applications.forEach(app => {
            const labName = app.LaboratoryName;
            if (labName) {
                labNames.add(labName);
            }
        });
        return Array.from(labNames).sort();
    };

    // Get unique States from applications
    const getUniqueStates = () => {
        const states = new Set();
        applications.forEach(app => {
            const state = app.State;
            if (state) {
                states.add(state);
            }
        });
        return Array.from(states).sort();
    };

    // Get unique Days Passed ranges
    const getDaysPassedOptions = () => {
        return [
            '0 days',
            '1 day',
            '2 days',
            '3+ days',
            '5+ days',
            '7+ days',
            '10+ days',
            '15+ days',
            '30+ days'
        ];
    };

    // Helper function to check if an application is a workable file
    const isWorkableFile = (app) => {
        const stage = app.Stage || app.Status || '';
        return stage === '18' || stage === '18b' || stage === '18c' || 
               stage === 18 || stage.toString().toLowerCase() === '18b' || 
               stage.toString().toLowerCase() === '18c';
    };

    // Helper function to check if days passed matches filter
    const matchesDaysPassedFilter = (app, filter) => {
        const stage = app.Stage || app.Status || '';
        const shouldCheckDays = stage === '18' || stage === '18b' || stage === '18c' || 
                                stage === 18 || stage.toString().toLowerCase() === '18b' || 
                                stage.toString().toLowerCase() === '18c';
        
        if (!shouldCheckDays) return false;

        const days = calculateDaysPassed(app.UpdateDate || app.StatusDate || app.InsertDate);
        if (typeof days !== 'number') return false;

        switch(filter) {
            case '0 days': return days === 0;
            case '1 day': return days === 1;
            case '2 days': return days === 2;
            case '3+ days': return days >= 3;
            case '5+ days': return days >= 5;
            case '7+ days': return days >= 7;
            case '10+ days': return days >= 10;
            case '15+ days': return days >= 15;
            case '30+ days': return days >= 30;
            default: return false;
        }
    };

    // Filter applications based on all filters
    useEffect(() => {
        let filtered = applications;

        // Apply workable files filter first
        if (workableFilesFilter) {
            filtered = filtered.filter(app => isWorkableFile(app));
        }

        // Apply status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(app => {
                const appStatus = app.Status || app.Stage;
                return appStatus === statusFilter;
            });
        }

        // Apply lab ID filter
        if (labIdFilter !== 'all') {
            filtered = filtered.filter(app => {
                const appLabId = app.ApplicationNo || app.TempAppNo || `TEMP-${app.AppId}`;
                return appLabId === labIdFilter;
            });
        }

        // Apply laboratory name filter
        if (labNameFilter !== 'all') {
            filtered = filtered.filter(app => {
                return app.LaboratoryName === labNameFilter;
            });
        }

        // Apply state filter
        if (stateFilter !== 'all') {
            filtered = filtered.filter(app => {
                return app.State === stateFilter;
            });
        }

        // Apply days passed filter
        if (daysPassedFilter !== 'all') {
            filtered = filtered.filter(app => {
                return matchesDaysPassedFilter(app, daysPassedFilter);
            });
        }

        setFilteredApplications(filtered);
    }, [applications, statusFilter, labIdFilter, labNameFilter, stateFilter, daysPassedFilter, workableFilesFilter]);

    // Handle status filter change
    const handleStatusFilterChange = (event) => {
        setStatusFilter(event.target.value);
    };

    // Load data on component mount
    useEffect(() => {
        checkAssessorDetails();
        loadApplications();
        loadLabTypes();
        loadCertificationFiles();
        checkSCFVisibility();
    }, []);

    // Calculate summary when applications change
    useEffect(() => {
        calculateSummary();
    }, [applications, calculateSummary]);

    // Refresh data
    const refreshData = () => {
        loadApplications();
        loadLabTypes();
        loadCertificationFiles();
        checkSCFVisibility();
    };

    useEffect(() => {
        const fetchTravelItineraryVisibility = async () => {
            if (applications.length === 0) return;
            try {
                const promises = applications.map(app => 
                    axios.get(`${process.env.REACT_APP_API_URL}/assessor-logistic/jd-acceptance-date/${app.AppId}`)
                        .then(res => ({ 
                            appId: app.AppId, 
                            isButtonVisible: res.data.data.isButtonVisible || false 
                        }))
                        .catch(() => ({ 
                            appId: app.AppId, 
                            isButtonVisible: false 
                        }))
                );
                const results = await Promise.all(promises);
                const map = {};
                results.forEach(r => { map[r.appId] = r.isButtonVisible; });
                setTravelItineraryButtonVisibilityMap(map);
            } catch (err) {
                console.error('Error fetching travel itinerary button visibility:', err);
            }
        };
        fetchTravelItineraryVisibility();
    }, [applications]);

    return (
      <Box sx={{ p: 3 }}>
        <ToastContainer />
        <Typography variant="h4" gutterBottom>
          Assessor Applications Management
        </Typography>

        {/* Summary Cards */}
        {summary && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Applications
                  </Typography>
                  <Typography variant="h4">
                    {summary.totalApplications}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    New Applications
                                </Typography>
                                <Typography variant="h4" color="primary">
                                    {summary.newApplications}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid> */}

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  border: workableFilesFilter
                    ? "2px solid #ff9800"
                    : "1px solid #e0e0e0",
                  backgroundColor: workableFilesFilter ? "#fff3e0" : "white",
                  "&:hover": {
                    boxShadow: 4,
                    transform: "translateY(-2px)",
                  },
                }}
                onClick={() => {
                  setWorkableFilesFilter(!workableFilesFilter);
                  // Reset other filters when workable files is selected
                  if (!workableFilesFilter) {
                    setStatusFilter("all");
                    setLabIdFilter("all");
                    setLabNameFilter("all");
                    setStateFilter("all");
                    setDaysPassedFilter("all");
                  }
                }}
              >
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Workable Files
                    {workableFilesFilter && (
                      <Chip
                        label="Active Filter"
                        size="small"
                        color="warning"
                        sx={{ ml: 1, height: "16px", fontSize: "0.6rem" }}
                      />
                    )}
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {summary.workableFiles || 0}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 1 }}
                  >
                    Click to filter
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Files at Lab End
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {summary.filesAtLabEnd || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Accredited Applications
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {summary.accreditedApplications}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Action Buttons */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Button
            variant={selectedAppType === "new" ? "contained" : "outlined"}
            sx={{
              backgroundColor:
                selectedAppType === "new" ? "#2196F3" : "transparent",
              "&:hover": {
                backgroundColor:
                  selectedAppType === "new" ? "#1976D2" : "#e3f2fd",
              },
            }}
            onClick={() => handleButtonClick("new")}
            startIcon={<AddCircleOutlineIcon />}
          >
            New Applications
          </Button>

          <Button
            variant={
              selectedAppType === "mainAssessment" ? "contained" : "outlined"
            }
            sx={{
              backgroundColor:
                selectedAppType === "mainAssessment"
                  ? "#FF9800"
                  : "transparent",
              "&:hover": {
                backgroundColor:
                  selectedAppType === "mainAssessment" ? "#F57C00" : "#fff3e0",
              },
            }}
            onClick={() => handleButtonClick("mainAssessment")}
            startIcon={<AccessTimeIcon />}
          >
            Main Assessment
          </Button>

          <Button
            variant={selectedAppType === "completed" ? "contained" : "outlined"}
            sx={{
              backgroundColor:
                selectedAppType === "completed" ? "#4CAF50" : "transparent",
              "&:hover": {
                backgroundColor:
                  selectedAppType === "completed" ? "#388E3C" : "#e8f5e8",
              },
            }}
            onClick={() => handleButtonClick("completed")}
            startIcon={<VerifiedUserIcon />}
          >
            Completed Applications
          </Button>

          <Box sx={{ flexGrow: 1 }} />

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Lab Type</InputLabel>
            <Select
              value={selectedLabType}
              label="Lab Type"
              onChange={(e) => {
                setSelectedLabType(e.target.value);
                setTimeout(() => loadApplications(selectedAppType), 100);
              }}
            >
              <MenuItem value={0}>All Lab Types</MenuItem>
              {labTypes.map((lt) => (
                <MenuItem key={lt.LabTypeId} value={lt.LabTypeId}>
                  {lt.LabType}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
            sx={{ minWidth: 250 }}
          />

          <Button variant="outlined" onClick={handleSearch} disabled={loading}>
            Search
          </Button>

          <Button
            variant="outlined"
            onClick={handleExportToExcel}
            startIcon={<GetAppIcon />}
            disabled={loading || applications.length === 0}
          >
            Export to Excel
          </Button>

          <Button
            variant="outlined"
            onClick={refreshData}
            startIcon={<RefreshIcon />}
            disabled={loading}
          >
            Refresh
          </Button>

          <Button
            variant="contained"
            color="success"
            onClick={() => {
              const targetUrl = `https://nablwpnew.qci.org.in/SCF_Integration/Transfer.aspx?user=Assessor&orgid=${localStorage.getItem("orgId")}`;
              window.location.href = targetUrl;
            }}
            sx={{ ml: 1 }}
          >
            SCF
          </Button>
        </Box>

        {/* Filter Section */}
        {!loading && applications.length > 0 && (
          <Box
            sx={{
              mb: 3,
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <Autocomplete
              sx={{ minWidth: 250 }}
              size="small"
              options={["All Statuses", ...getUniqueStatuses()]}
              value={statusFilter === "all" ? "All Statuses" : statusFilter}
              onChange={(event, newValue) => {
                setStatusFilter(
                  newValue === "All Statuses" || newValue === null
                    ? "all"
                    : newValue,
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Status"
                  placeholder="Type or select status"
                />
              )}
              isOptionEqualToValue={(option, value) => option === value}
              clearOnEscape
              disableClearable={false}
            />

            <Autocomplete
              sx={{ minWidth: 250 }}
              size="small"
              options={["All Laboratories", ...getUniqueLabNames()]}
              value={
                labNameFilter === "all" ? "All Laboratories" : labNameFilter
              }
              onChange={(event, newValue) => {
                setLabNameFilter(
                  newValue === "All Laboratories" || newValue === null
                    ? "all"
                    : newValue,
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Laboratory Name"
                  placeholder="Type or select lab name"
                />
              )}
              isOptionEqualToValue={(option, value) => option === value}
              clearOnEscape
              disableClearable={false}
            />

            <Autocomplete
              sx={{ minWidth: 200 }}
              size="small"
              options={["All States", ...getUniqueStates()]}
              value={stateFilter === "all" ? "All States" : stateFilter}
              onChange={(event, newValue) => {
                setStateFilter(
                  newValue === "All States" || newValue === null
                    ? "all"
                    : newValue,
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by State"
                  placeholder="Type or select state"
                />
              )}
              isOptionEqualToValue={(option, value) => option === value}
              clearOnEscape
              disableClearable={false}
            />

            {(workableFilesFilter ||
              statusFilter !== "all" ||
              labIdFilter !== "all" ||
              labNameFilter !== "all" ||
              stateFilter !== "all" ||
              daysPassedFilter !== "all") && (
              <Chip
                label={`Filtered: ${filteredApplications.length} of ${applications.length}${workableFilesFilter ? " (Workable Files)" : ""}`}
                color="primary"
                variant="outlined"
                onDelete={() => {
                  setWorkableFilesFilter(false);
                  setStatusFilter("all");
                  setLabIdFilter("all");
                  setLabNameFilter("all");
                  setStateFilter("all");
                  setDaysPassedFilter("all");
                }}
              />
            )}
          </Box>
        )}

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Success Display */}
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Applications Table */}
        {!loading && applications.length > 0 && (
          <>
            <Box
              sx={{
                mb: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: "medium" }}>
                {AssessorApiService.getAppTypeDisplayName(selectedAppType)}
                <Chip
                  label={
                    statusFilter === "all"
                      ? `${applications.length} records`
                      : `${filteredApplications.length} of ${applications.length} records`
                  }
                  size="small"
                  sx={{ ml: 1 }}
                  color="primary"
                  variant="outlined"
                />
              </Typography>
            </Box>

            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 3,
                borderRadius: 2,
                overflow: "auto",
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#f5f5f5",
                        width: 80,
                        textAlign: "center",
                      }}
                    >
                      Sr. No. / Actions
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#f5f5f5",
                        minWidth: 200,
                      }}
                    >
                      Lab Details
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#f5f5f5",
                        minWidth: 250,
                      }}
                    >
                      Location Details
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "bisque",
                        minWidth: 120,
                      }}
                    >
                      Status
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#f5f5f5",
                        minWidth: 150,
                      }}
                    >
                      Assessment Start Date
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#f5f5f5",
                        minWidth: 150,
                      }}
                    >
                      Assessment End Date
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredApplications.map((app, index) => (
                    <TableRow
                      key={app.AppId}
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: "#fafafa",
                        },
                        "&:hover": {
                          backgroundColor: "#f0f8ff",
                          cursor: "pointer",
                        },
                      }}
                    >
                      <TableCell sx={{ textAlign: "center", width: 120 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "medium", fontSize: "0.75rem" }}
                          >
                            {index + 1}
                          </Typography>
                          {(app.AssessorAccepted === 0 ||
                            app.AssessorAccepted === "") &&
                            app.Stage === "19c" && (
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 0.5,
                                  flexDirection: "row",
                                  justifyContent: "center",
                                }}
                              >
                                <Tooltip title="Accept Application">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    sx={{
                                      backgroundColor: "#e8f5e9",
                                      "&:hover": { backgroundColor: "#c8e6c9" },
                                    }}
                                    onClick={() => handleAccept(app)}
                                  >
                                    <CheckIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Reject Application">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    sx={{
                                      backgroundColor: "#ffebee",
                                      "&:hover": { backgroundColor: "#ffcdd2" },
                                    }}
                                    onClick={() => handleReject(app)}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          {(app.Stage === "20" || app.Stage === "21") && (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                flexDirection: "row",
                                justifyContent: "center",
                                mt: 0.5,
                              }}
                            >
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                sx={{
                                  backgroundColor: "#e3f2fd",
                                  "&:hover": { backgroundColor: "#bbdefb" },
                                }}
                                onClick={() =>
                                  navigate(
                                    `/main/assessor/assessment-dashboard?appId=${encryptAppId(app.AppId)}&orgId=${encryptText(app.OrgId)}&assessorId=${encryptText(orgId)}`,
                                  )
                                }
                              >
                                View
                              </Button>
                            </Box>
                          )}
                          {travelItineraryButtonVisibilityMap[app.AppId] && (
                          <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                flexDirection: "row",
                                justifyContent: "center",
                                mt: 0.5,
                              }}
                            >
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                sx={{
                                  backgroundColor: "#e3f2fd",
                                  "&:hover": { backgroundColor: "#bbdefb" },
                                }}
                                onClick={() =>
                                  {
                                    const assessmentDetails = {
                                      ApplicationNo: app.ApplicationNo,
                                      LaboratoryName: app.LaboratoryName,
                                      LoginName: app.LoginName,
                                      Address: app.Address,
                                      AssessmentType: app.AssessmentType,
                                      AssessmentDate: app.AssessmentDate,
                                      assessmentId: app.assessmentId || ""
                                    };
                                    navigate(
                                      `/main/assessor/assessor-logistic-details?appId=${encryptAppId(app.AppId)}&orgId=${encryptText(app.OrgId)}
                                      &assessorId=${encryptText(orgId)}&assessmentDetail=${encryptText(JSON.stringify(assessmentDetails)
                                     )}`,
                                    );
                                  }
                                }
                              >
                               Travel Itinerary
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.875rem" }}
                          >
                            Name: {app.LaboratoryName || "N/A"}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.875rem" }}
                            >
                              Standard:
                            </Typography>
                            <Chip
                              label={app.Standard || ""}
                              size="small"
                              variant="outlined"
                              color="info"
                              sx={{ height: "20px", fontSize: "0.75rem" }}
                            />
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Chip
                              label={
                                app.AssessmentType ||
                                getApplicationTypeName(app.AssessmentTypeId)
                              }
                              size="small"
                              color={
                                app.AssessmentTypeId === 1
                                  ? "primary"
                                  : "success"
                              }
                              variant="filled"
                              sx={{ height: "20px", fontSize: "0.75rem" }}
                            />
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Chip
                              label={app.ApplicationNo || ""}
                              size="small"
                              variant="outlined"
                              color="info"
                              sx={{ height: "20px", fontSize: "0.75rem" }}
                            />
                            <Chip
                              label={app.LabType || "N/A"}
                              size="small"
                              color={app.LabType ? "primary" : "warning"}
                              variant="filled"
                              sx={{ height: "20px", fontSize: "0.75rem" }}
                            />
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            {(() => {
                              // Check if stage is 18, 18b, or 18c
                              const stage = app.Stage || app.Status || "";
                              const shouldShowDays =
                                stage === "18" ||
                                stage === "18b" ||
                                stage === "18c" ||
                                stage === 18 ||
                                stage.toString().toLowerCase() === "18b" ||
                                stage.toString().toLowerCase() === "18c";

                              if (!shouldShowDays) {
                                return null;
                              }

                              const days = calculateDaysPassed(
                                app.UpdateDate ||
                                  app.StatusDate ||
                                  app.InsertDate,
                              );
                              const isNumeric = typeof days === "number";
                              const bgColor = isNumeric
                                ? days > 2
                                  ? "#ffebee"
                                  : days > 1
                                    ? "#fff3e0"
                                    : "#e8f5e9"
                                : "#f5f5f5";
                              const textColor = isNumeric
                                ? days > 2
                                  ? "#c62828"
                                  : days > 1
                                    ? "#ef6c00"
                                    : "#2e7d32"
                                : "#757575";

                              return (
                                <Box
                                  sx={{
                                    p: 1,
                                    borderRadius: 1,
                                    backgroundColor: bgColor,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    minWidth: "80px",
                                  }}
                                >
                                  <AccessTimeIcon
                                    sx={{
                                      fontSize: "1rem",
                                      color: textColor,
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: "bold",
                                      fontSize: "0.875rem",
                                      color: textColor,
                                    }}
                                  >
                                    {isNumeric ? `${days} days Passed` : days}
                                  </Typography>
                                </Box>
                              );
                            })()}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.875rem", fontWeight: "medium" }}
                          >
                            {app.Address || "N/A"}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.5,
                              alignItems: "center",
                              flexWrap: "wrap",
                            }}
                          >
                            <Chip
                              label={app.City || "N/A"}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{
                                height: "20px",
                                fontSize: "0.7rem",
                                "& .MuiChip-label": { px: 1 },
                              }}
                            />
                            <Chip
                              label={app.State || "N/A"}
                              size="small"
                              variant="filled"
                              color="secondary"
                              sx={{
                                height: "20px",
                                fontSize: "0.7rem",
                                "& .MuiChip-label": { px: 1 },
                              }}
                            />
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={app.Status || app.Stage || "Pending"}
                          color="warning"
                          size="small"
                          variant="filled"
                          sx={{
                            height: "auto",
                            "& .MuiChip-label": {
                              whiteSpace: "normal",
                              wordWrap: "break-word",
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {(() => {
                            const dates = parseAssessmentDate(
                              app.AssessmentDate,
                            );
                            return dates.start || "Not Scheduled";
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {(() => {
                            const dates = parseAssessmentDate(
                              app.AssessmentDate,
                            );
                            return dates.end || "Not Scheduled";
                          })()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mt: 2, textAlign: "center" }}
            >
              Showing {filteredApplications.length} of {applications.length}{" "}
              applications
              {(workableFilesFilter ||
                statusFilter !== "all" ||
                labIdFilter !== "all" ||
                labNameFilter !== "all" ||
                stateFilter !== "all" ||
                daysPassedFilter !== "all") && (
                <span style={{ color: "#1976d2", fontWeight: "bold" }}>
                  {" "}
                  (Filtered by:
                  {workableFilesFilter && " Workable Files"}
                  {workableFilesFilter &&
                    (statusFilter !== "all" ||
                      labIdFilter !== "all" ||
                      labNameFilter !== "all" ||
                      stateFilter !== "all" ||
                      daysPassedFilter !== "all") &&
                    ","}
                  {statusFilter !== "all" && ` Status: ${statusFilter}`}
                  {statusFilter !== "all" &&
                    (labIdFilter !== "all" ||
                      labNameFilter !== "all" ||
                      stateFilter !== "all" ||
                      daysPassedFilter !== "all") &&
                    ","}
                  {labIdFilter !== "all" && ` Lab ID: ${labIdFilter}`}
                  {labIdFilter !== "all" &&
                    (labNameFilter !== "all" ||
                      stateFilter !== "all" ||
                      daysPassedFilter !== "all") &&
                    ","}
                  {labNameFilter !== "all" && ` Lab: ${labNameFilter}`}
                  {labNameFilter !== "all" &&
                    (stateFilter !== "all" || daysPassedFilter !== "all") &&
                    ","}
                  {stateFilter !== "all" && ` State: ${stateFilter}`}
                  {stateFilter !== "all" && daysPassedFilter !== "all" && ","}
                  {daysPassedFilter !== "all" && ` Days: ${daysPassedFilter}`})
                </span>
              )}
            </Typography>
          </>
        )}

        {/* No Data State */}
        {!loading && applications.length === 0 && !error && (
          <Paper sx={{ p: 8, textAlign: "center", borderRadius: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <AddCircleOutlineIcon sx={{ fontSize: 64, color: "grey.400" }} />
              <Typography variant="h6" color="textSecondary">
                No applications found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {selectedAppType === "new"
                  ? "No new applications or pre-assessments are currently assigned to you."
                  : selectedAppType === "mainAssessment"
                    ? "No main assessment applications are currently assigned to you."
                    : "No completed applications are currently assigned to you."}
              </Typography>
              <Button
                variant="outlined"
                onClick={refreshData}
                startIcon={<RefreshIcon />}
                sx={{ mt: 2 }}
              >
                Refresh Data
              </Button>
            </Box>
          </Paper>
        )}

        {/* Application Details Dialog */}
        <Dialog
          open={detailsDialogOpen}
          onClose={() => setDetailsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Application Details</DialogTitle>
          <DialogContent>
            {selectedApplication && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Laboratory Name:</Typography>
                  <Typography variant="body2">
                    {selectedApplication.LaboratoryName ||
                      selectedApplication.laboratoryName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Application ID:</Typography>
                  <Typography variant="body2">
                    {selectedApplication.appid ||
                      selectedApplication.ApplicationNo}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Contact Person:</Typography>
                  <Typography variant="body2">
                    {selectedApplication.LoginName ||
                      selectedApplication.contactPerson}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Contact Email:</Typography>
                  <Typography variant="body2">
                    {selectedApplication.contactEmail || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Contact Mobile:</Typography>
                  <Typography variant="body2">
                    {selectedApplication.contactMobile || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Application Date:</Typography>
                  <Typography variant="body2">
                    {formatDate(
                      selectedApplication.SubmitDate ||
                        selectedApplication.applicationDate,
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Address:</Typography>
                  <Typography variant="body2">
                    {selectedApplication.Address ||
                      selectedApplication.address ||
                      "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Accept Confirmation Dialog */}
        <Dialog
          open={acceptDialogOpen}
          onClose={handleAcceptDialogClose}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Declaration and Acceptance</DialogTitle>
          <DialogContent>
            {selectedAppForAction && (
              <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1, mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Laboratory: {selectedAppForAction.LaboratoryName}
                </Typography>
                <Typography variant="body2">
                  Application No:{" "}
                  {selectedAppForAction.ApplicationNo ||
                    selectedAppForAction.TempAppNo}
                </Typography>
              </Box>
            )}

            <Typography
              variant="h6"
              sx={{ mb: 2, color: "primary.main", fontWeight: "bold" }}
            >
              I hereby declare the following:
            </Typography>

            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={declaration1Checked}
                    onChange={(e) => setDeclaration1Checked(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body1">
                    <Box component="span" sx={{ fontWeight: "bold", mr: 1 }}>
                      1.
                    </Box>
                    I have not offered consultancy, guidance, supervision or
                    other services (e.g. Internal audit, training, etc.) to this
                    CAB.
                  </Typography>
                }
                sx={{ alignItems: "flex-start", mb: 2, display: "flex" }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={declaration2Checked}
                    onChange={(e) => setDeclaration2Checked(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body1">
                    <Box component="span" sx={{ fontWeight: "bold", mr: 1 }}>
                      2.
                    </Box>
                    I have no professional and / or personal relation with the
                    CAB management and/ or any member of CAB’s staff.
                  </Typography>
                }
                sx={{ alignItems: "flex-start", mb: 2, display: "flex" }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={declaration3Checked}
                    onChange={(e) => setDeclaration3Checked(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body1">
                    <Box component="span" sx={{ fontWeight: "bold", mr: 1 }}>
                      3.
                    </Box>
                    I have not been deputed to this CAB earlier
                  </Typography>
                }
                sx={{ alignItems: "flex-start", mb: 2, display: "flex" }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={declaration4Checked}
                    onChange={(e) => setDeclaration4Checked(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body1">
                    <Box component="span" sx={{ fontWeight: "bold", mr: 1 }}>
                      4.
                    </Box>
                    I am working in NABL accredited laboratory as specified in
                    my assessor profile.
                  </Typography>
                }
                sx={{ alignItems: "flex-start", mb: 2, display: "flex" }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleAcceptDialogClose}
              disabled={loading}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmAccept}
              variant="contained"
              color="success"
              disabled={
                loading ||
                !declaration1Checked ||
                !declaration2Checked ||
                !declaration3Checked ||
                !declaration4Checked
              }
              startIcon={
                loading ? <CircularProgress size={20} /> : <CheckIcon />
              }
              size="large"
            >
              {loading ? "Accepting..." : "Accept All and Proceed"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reject Confirmation Dialog */}
        <Dialog
          open={rejectDialogOpen}
          onClose={() => !loading && setRejectDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Reject Application</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Please provide remarks for rejecting this application:
            </Typography>
            {selectedAppForAction && (
              <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Laboratory: {selectedAppForAction.LaboratoryName}
                </Typography>
              </Box>
            )}
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Remarks *"
              placeholder="Enter reason for rejection..."
              value={rejectRemarks}
              onChange={(e) => setRejectRemarks(e.target.value)}
              disabled={loading}
              error={!rejectRemarks && error}
              helperText={!rejectRemarks && error ? "Remarks are required" : ""}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setRejectDialogOpen(false);
                setRejectRemarks("");
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmReject}
              variant="contained"
              color="error"
              disabled={loading || !rejectRemarks.trim()}
              startIcon={
                loading ? <CircularProgress size={20} /> : <CloseIcon />
              }
            >
              {loading ? "Rejecting..." : "Reject"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
};
export default SubmittedApplicationSection;
